import { useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Modal } from '@mui/material';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import { t } from 'i18next';

import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { PATH } from '~/constants/route-path';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { COLORS } from '~/styles/colors';

export const SubscriptionPopup = ({ endTime, onClose, open }) => {
  const [date, setDate] = useState({ days: 0, hours: 0, minutes: 0 });
  const { data: accountConfig } = useSelector(getLoggedInUser);

  const givenDate = useMemo(() => {
    return new Date(endTime);
  }, [endTime]);

  const setTime = () => {
    const now = new Date();

    const days = differenceInDays(givenDate, now);
    const hours = differenceInHours(givenDate, now) % 24;
    const minutes = differenceInMinutes(givenDate, now) % 60;

    setDate({
      days: Math.max(0, days),
      hours: Math.max(0, hours),
      minutes: Math.max(0, minutes),
    });
  };

  const initTimer = () => {
    setTime();

    return setInterval(() => {
      setTime();
    }, 1000);
  };

  const navigate = useNavigate();

  useLayoutEffect(() => {
    const intervalId = initTimer();

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  if (!accountConfig) {
    return null;
  }

  return (
    <Modal
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      open={open}
      onClose={() => onClose(false)}
    >
      <Box
        sx={{
          background: COLORS.white,
          padding: 5,
          borderRadius: '20px',
          maxWidth: 475,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <CloseIcon sx={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={() => onClose(false)} />
        <Text variant="h2">{t('expirePopup:title')}</Text>

        <Box sx={[!givenDate.getMonth() && { mb: 5 }, { mt: '16px' }]} display="flex" justifyContent="center">
          <Box mr={5} textAlign="center">
            <Text variant="h1">{date?.days || '0'}</Text>
            <Text variant="input15" sx={{ color: COLORS.grey400 }}>
              {t('expirePopup:days')}
            </Text>
          </Box>
          <Box textAlign="center" mr={5}>
            <Text variant="h1">{date?.hours || '0'}</Text>
            <Text variant="input15" sx={{ color: COLORS.grey400 }}>
              {t('expirePopup:hours')}
            </Text>
          </Box>
          <Box textAlign="center">
            <Text variant="h1">{date?.minutes || '0'}</Text>
            <Text variant="input15" sx={{ color: COLORS.grey400 }}>
              {t('expirePopup:minutes')}
            </Text>
          </Box>
        </Box>

        {!!date?.minutes && (
          <Text variant="input15" sx={{ textAlign: 'center', margin: '24px 0', color: COLORS.grey400 }}>
            {t('expirePopup:after')}{' '}
            <span style={{ color: COLORS.black, fontWeight: 600 }}>
              {t(`month:${givenDate.getMonth() + 1}`)} {givenDate.getDate()}, {givenDate.getFullYear()}
            </span>
            {t('expirePopup:afterNext')}
          </Text>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <CancelIcon sx={{ color: COLORS.errorDark }} />
            <Text variant="input15">{t('expirePopup:text1')}</Text>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <CancelIcon sx={{ color: COLORS.errorDark }} />
            <Text variant="input15">{t('expirePopup:text2')}</Text>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <CancelIcon sx={{ color: COLORS.errorDark }} />
            <Text variant="input15">{t('expirePopup:text3')}</Text>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <CancelIcon sx={{ color: COLORS.errorDark }} />
            <Text variant="input15">{t('expirePopup:text4')}</Text>
          </Box>
        </Box>

        <Text variant="inputVerySmall" sx={{ color: COLORS.grey400, textAlign: 'center', marginTop: '24px' }}>
          {t('expirePopup:bottomText')}
        </Text>

        <Button
          onClick={() => {
            navigate(PATH.PAYMENTS.MAIN);
            onClose(false);
          }}
          sx={{ background: COLORS.purple, marginTop: '24px', textTransform: 'capitalize' }}
          variant="contained"
        >
          {t('buttons:upgradeNow')}
        </Button>
      </Box>
    </Modal>
  );
};
